var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Provider } from 'root/src/shared/@types/providers';
import { Platform } from 'root/src/shared/@types/platforms';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import { useUserData } from 'root/src/client/v2/userData/hooks/useUserData';
import { useUserId } from 'root/src/client/v2/auth/hooks/useUserId';
import { useSignInWithProvider } from 'root/src/client/web/auth/hooks/useSignInWithProvider';
import setPreferredPlatform from 'root/src/client/logic/static/thunks/setPreferredPlatform';
import removeAuthToken from 'root/src/client/logic/userData/thunks/removeAuthToken';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Badge } from 'root/src/client/v2/common/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogPortal } from 'root/src/client/v2/common/components/ui/dialog';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { GoogleIcon, TwitchIcon, TikTokIcon } from 'root/src/client/v2/common/components/icons/providerIcons';
const PROVIDERS = [
    { key: 'twitch', provider: Provider.Twitch, platform: Platform.Twitch, label: 'Twitch', description: 'Stream platform for live content', color: '#9146FF', Icon: TwitchIcon },
    { key: 'youtube', provider: Provider.Google, platform: Platform.YouTube, label: 'YouTube', description: 'YouTube & Google account', color: '#FF0000', Icon: GoogleIcon },
    { key: 'tiktok', provider: Provider.TikTok, platform: Platform.TikTok, label: 'TikTok', description: 'Short-form video platform', color: '#ffffff', Icon: TikTokIcon },
];
// Shared enter/exit for action items so they pop in/out smoothly (popLayout keeps siblings from jumping).
const itemMotion = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.18 },
};
// The platform tied to the primary login cannot be disconnected (mirrors legacy ChannelItem logic).
const canDisconnect = (platform, userId) => {
    if (userId.includes('google') && platform === Platform.YouTube) {
        return false;
    }
    return !new RegExp(platform, 'gi').test(userId);
};
const DEFAULT_INFO = 'The default channel is the one DareDrop uses as your main identity, for example for giveaway entries and on your public profile.';
// Type-to-confirm disconnect: the destructive action can lose keys / active dares, so we make
// the user type "disconnect" before enabling the button.
const DisconnectConfirmDialog = ({ open, onOpenChange, channelLabel, onConfirm }) => {
    const [value, setValue] = useState('');
    const canConfirm = value.trim().toLowerCase() === 'disconnect';
    useEffect(() => {
        if (!open) {
            setValue('');
        }
    }, [open]);
    const confirm = () => {
        if (!canConfirm) {
            return;
        }
        onOpenChange(false);
        onConfirm();
    };
    return (<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPortal>
				{/* Glass card matching the other redesign modals, z above the settings dialog (1100/1101). */}
				<DialogPrimitive.Overlay className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm data-[state=open]:animate-dialog-overlay-show data-[state=closed]:animate-dialog-overlay-hide"/>
				<DialogPrimitive.Content className="redesign fixed left-1/2 top-1/2 z-[1201] flex max-h-[90vh] w-[95vw] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-6 overflow-auto rounded-2xl border border-solid border-white/[0.08] bg-card p-6 text-foreground shadow-2xl backdrop-blur-xl data-[state=closed]:animate-dialog-content-hide data-[state=open]:animate-dialog-content-show sm:w-full sm:min-w-[26rem] sm:max-w-md sm:p-8">
					<div className="flex w-full flex-col items-center gap-3 text-center">
						<div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-destructive/30 bg-destructive/10">
							<WarningAmberIcon sx={{ fontSize: 24 }} className="text-destructive"/>
						</div>
						<DialogPrimitive.Title className="font-display text-xl font-bold text-foreground">Disconnect {channelLabel}?</DialogPrimitive.Title>
						<DialogPrimitive.Description className="text-sm text-muted-foreground">
							This can lose account data tied to {channelLabel}, such as game keys or active dares. This cannot be undone.
						</DialogPrimitive.Description>
					</div>
					<div className="w-full">
						<label htmlFor="disconnect-confirm" className="mb-1.5 block text-[11px] font-medium text-muted-foreground">
							Type <span className="font-semibold text-foreground">disconnect</span> to confirm
						</label>
						<Input id="disconnect-confirm" value={value} onChange={event => setValue(event.target.value)} placeholder="disconnect" autoFocus onKeyDown={(event) => { if (event.key === 'Enter')
        confirm(); }} className="w-full"/>
					</div>
					<div className="flex w-full gap-2">
						<Button variant="secondary" onClick={() => onOpenChange(false)} className="flex-1 rounded-lg text-sm">Cancel</Button>
						<Button variant="destructive" disabled={!canConfirm} onClick={confirm} className="flex-1 rounded-lg text-sm">Disconnect</Button>
					</div>
				</DialogPrimitive.Content>
			</DialogPortal>
		</Dialog>);
};
const ChannelRow = ({ config, token, userId, index, disconnecting, settingDefault, onConnected, onDisconnect, onSetDefault }) => {
    // Connect via OIDC popup (matches the current app's channel-linking flow), refresh on success.
    const { onSignIn: connect, isLoading: connecting } = useSignInWithProvider({
        provider: config.provider,
        isLinking: true,
        popup: true,
        popupOptions: {
            onSuccess: () => __awaiter(void 0, void 0, void 0, function* () {
                yield onConnected();
                toast.success(`${config.label} connected`);
            }),
            onError: (error) => {
                toast.error(`Could not connect ${config.label}`, { description: error.message });
            },
        },
    });
    const { Icon } = config;
    const isConnected = !!token;
    const isDefault = !!(token === null || token === void 0 ? void 0 : token.isPreferred);
    const showDisconnect = isConnected && canDisconnect(config.platform, userId);
    const [confirmOpen, setConfirmOpen] = useState(false);
    return (<motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.06, ease: 'easeOut' }} className={cn('rounded-xl border border-solid p-4 transition-colors', isConnected ? 'border-white/[0.08] bg-secondary/30' : 'border-dashed border-border/50 bg-secondary/10')}>
			<div className="flex flex-col gap-3 md:flex-row md:items-center">
				<div className="flex min-w-0 items-center gap-3 md:flex-1">
					<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-solid transition-colors" style={{
            borderColor: isConnected ? `${config.color}30` : 'hsl(var(--border))',
            backgroundColor: isConnected ? `${config.color}10` : undefined,
            color: isConnected ? config.color : 'hsl(var(--muted-foreground))',
        }}>
						<Icon className="h-5 w-5"/>
					</div>
					<div className="min-w-0 flex-1">
						<p className="text-xs font-semibold">{config.label}</p>
						<p className="truncate text-[11px] text-muted-foreground">
							{isConnected ? ((token === null || token === void 0 ? void 0 : token.displayName) || (token === null || token === void 0 ? void 0 : token.mnemonicId)) : config.description}
						</p>
					</div>
				</div>
				<div className="flex flex-shrink-0 items-center justify-end gap-1.5">
					<AnimatePresence mode="popLayout" initial={false}>
						{isConnected && (<motion.div layout key="connected" {...itemMotion}>
								<Badge className="border-neon-green/20 bg-neon-green/10 px-1.5 py-0 text-[9px] text-neon-green">Connected</Badge>
							</motion.div>)}
						{isDefault && (<motion.div layout key="default" {...itemMotion}>
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="cursor-default">
											<Badge className="border-neon-cyan/20 bg-neon-cyan/10 px-1.5 py-0 text-[9px] text-neon-cyan">Default</Badge>
										</span>
									</TooltipTrigger>
									<TooltipContent className="max-w-[220px]">{DEFAULT_INFO}</TooltipContent>
								</Tooltip>
							</motion.div>)}
						{isConnected && !isDefault && (<motion.div layout key="set-default" {...itemMotion}>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button size="icon" variant="ghost" loading={settingDefault} onClick={onSetDefault} aria-label="Set as default" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-neon-cyan">
											<StarBorderIcon sx={{ fontSize: 16 }}/>
										</Button>
									</TooltipTrigger>
									<TooltipContent>Set as default</TooltipContent>
								</Tooltip>
							</motion.div>)}
						{isConnected && showDisconnect && (<motion.div layout key="disconnect" {...itemMotion}>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button size="icon" variant="ghost" loading={disconnecting} onClick={() => setConfirmOpen(true)} aria-label="Disconnect" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive">
											<LinkOffIcon sx={{ fontSize: 16 }}/>
										</Button>
									</TooltipTrigger>
									<TooltipContent>Disconnect</TooltipContent>
								</Tooltip>
								<DisconnectConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} channelLabel={config.label} onConfirm={onDisconnect}/>
							</motion.div>)}
						{!isConnected && (<motion.div layout key="connect" {...itemMotion}>
								<Button size="sm" variant="outline" loading={connecting} onClick={connect} className="h-7 gap-1.5 rounded-lg px-2.5 text-[11px] font-medium">
									<LinkIcon sx={{ fontSize: 14 }}/>
									Connect
								</Button>
							</motion.div>)}
					</AnimatePresence>
				</div>
			</div>
		</motion.div>);
};
export const ConnectedChannelsPanel = () => {
    var _a;
    const dispatch = useAppDispatch();
    const { userId } = useUserId();
    const { userData, invalidateUserData } = useUserData();
    const [disconnectingKey, setDisconnectingKey] = useState(null);
    const [settingDefaultKey, setSettingDefaultKey] = useState(null);
    const tokens = (_a = userData === null || userData === void 0 ? void 0 : userData.tokens) !== null && _a !== void 0 ? _a : [];
    const tokenFor = (platform) => tokens.find(t => t.platform === platform);
    const handleDisconnect = (config, token) => {
        setDisconnectingKey(config.key);
        const promise = dispatch(removeAuthToken(token.platform, token.platformId)).then(() => invalidateUserData());
        toast.promise(promise, {
            loading: `Disconnecting ${config.label}...`,
            success: `${config.label} disconnected`,
            error: `Could not disconnect ${config.label}. Please try again.`,
        });
        promise.then(() => setDisconnectingKey(null), () => setDisconnectingKey(null));
    };
    const handleSetDefault = (config, token) => {
        setSettingDefaultKey(config.key);
        const promise = dispatch(setPreferredPlatform(token.platform)).then(() => invalidateUserData());
        toast.promise(promise, {
            loading: `Setting ${config.label} as default...`,
            success: `${config.label} set as default`,
            error: 'Could not set default. Please try again.',
        });
        promise.then(() => setSettingDefaultKey(null), () => setSettingDefaultKey(null));
    };
    return (<div className="space-y-6">
			<div>
				<h3 className="mb-1 text-sm font-semibold">Connected Channels</h3>
				<p className="mb-4 text-xs text-muted-foreground">
					Link your accounts to enable giveaway entry verification and stream integrations.
				</p>
				<TooltipProvider delayDuration={200}>
					<div className="space-y-2.5">
						{PROVIDERS.map((config, index) => {
            const token = tokenFor(config.platform);
            return (<ChannelRow key={config.key} config={config} token={token} userId={userId !== null && userId !== void 0 ? userId : ''} index={index} disconnecting={disconnectingKey === config.key} settingDefault={settingDefaultKey === config.key} onConnected={invalidateUserData} onDisconnect={() => token && handleDisconnect(config, token)} onSetDefault={() => token && handleSetDefault(config, token)}/>);
        })}
					</div>
				</TooltipProvider>
			</div>
			<div className="space-y-2 rounded-xl border border-solid border-border/30 bg-secondary/10 p-3">
				<p className="text-[11px] leading-relaxed text-muted-foreground">
					<span className="font-medium text-foreground">Why connect?</span>{' '}
					Linked channels let you verify giveaway entries (follows, subs) automatically and unlock stream overlay integrations.
				</p>
				<p className="text-[11px] leading-relaxed text-muted-foreground">
					<span className="font-medium text-foreground">Default channel</span>{' '}
					{DEFAULT_INFO}
				</p>
			</div>
		</div>);
};
